import React, { useEffect, useState } from 'react';
import axiosInstant from '../../api';
import { pricingUsageRatesEndpoint } from '../../api/endpoints';
import { BLACK, BORDER, CARD_BG, SURFACE_2, TEXT_MUTED } from '../../components/ProposalFlow/theme';
import type { UsageKind, UsageRate } from '../../types/pricingPlans';

interface Props {
  headingColor: string;
  mutedColor: string;
}

// Client-facing names for the metered units (the rate card's own labels are
// admin wording — "audit", "prep pack"). Kinds are code-enumerated in the API.
// Per-run caps the ranges are built from (API config defaults): a brand
// Consumer Voice run analyzes up to 10 products (DL_CV_BRAND_MAX_PRODUCTS);
// a category / brand report analyzes up to 200 pages (REPORT_MAX_PRODUCTS).
const CV_BRAND_MAX_PRODUCTS = 10;
const REPORT_MAX_PAGES = 200;
// A prep pack analyzes 1–5 product pages (MAX_PRODUCT_URLS).
const PREP_PACK_MAX_PRODUCTS = 5;

type Price = (k: UsageKind) => number;
// A row's rate: one price, or a [min, max] range when one charge pulls in
// others (a brand report plus its product analyses) or the cost depends on
// how the run is done.
interface UnitCopy {
  label: string;
  per: string;
  range?: (price: Price) => [number, number];
}

const UNIT_COPY: Partial<Record<UsageKind, UnitCopy>> = {
  audit: { label: 'PDP analysis', per: 'per product, per run' },
  consumer_voice: { label: 'Product Consumer Voice Analysis', per: 'per product, per run' },
  consumer_voice_report: {
    label: 'Consumer Voice Analysis, per brand',
    per: `per brand report, plus a Consumer Voice analysis for each product (1–${CV_BRAND_MAX_PRODUCTS})`,
    range: (price) => [
      price('consumer_voice_report') + price('consumer_voice'),
      price('consumer_voice_report') + CV_BRAND_MAX_PRODUCTS * price('consumer_voice'),
    ],
  },
  ai_presence: {
    label: 'Product AI Presence Analysis',
    per: "per product, per run: lower when the product shares its category's questions with the brand's other products that week",
    range: (price) => [price('ai_presence'), price('ai_presence_standalone')],
  },
  prep_pack: {
    label: 'Sales Intel Prep Pack',
    per: `per pack: brand intelligence report, plus a PDP, Consumer Voice and AI Presence analysis for each product (1–${PREP_PACK_MAX_PRODUCTS})`,
    range: (price) => [
      price('prep_pack') + price('audit') + price('consumer_voice') + price('ai_presence'),
      price('prep_pack') +
        PREP_PACK_MAX_PRODUCTS * (price('audit') + price('consumer_voice') + price('ai_presence_standalone')),
    ],
  },
  catalog_report: {
    label: 'Category or brand discovery & analysis',
    per: `per report, plus a PDP analysis for each page (1–${REPORT_MAX_PAGES})`,
    range: (price) => [
      price('catalog_report') + price('audit'),
      price('catalog_report') + REPORT_MAX_PAGES * price('audit'),
    ],
  },
};
const ORDER: UsageKind[] = ['audit', 'consumer_voice', 'consumer_voice_report', 'ai_presence', 'prep_pack', 'catalog_report'];

const money = (n: number) => `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// "Beyond your plan": the usage rate card (brand_usage_rates, admin >
// Pricing > Usage Rates) as the overage price list. Plans include
// allowances; the usage ledger meters anything past them at these rates.
// Enterprise overage stays per contract (footnote).
export function UsageRatesSection({ headingColor, mutedColor }: Props) {
  const [rates, setRates] = useState<UsageRate[] | null>(null);

  useEffect(() => {
    let mounted = true;
    axiosInstant
      .get(pricingUsageRatesEndpoint)
      .then((res) => {
        if (mounted) setRates((res.data && res.data.rates) || []);
      })
      .catch(() => {
        if (mounted) setRates([]);
      });
    return () => {
      mounted = false;
    };
  }, []);

  if (!rates || rates.length === 0) return null;
  const byKind: Partial<Record<UsageKind, UsageRate>> = {};
  rates.forEach((r) => {
    byKind[r.kind] = r;
  });
  const rows = ORDER.filter((k) => byKind[k] && UNIT_COPY[k]);
  const price: Price = (k) => Number((byKind[k] && byKind[k]!.unit_price) || 0);
  const rateLabel = (k: UsageKind) => {
    const copy = UNIT_COPY[k] as UnitCopy;
    if (!copy.range) return money(price(k));
    const [lo, hi] = copy.range(price);
    return hi > lo ? `${money(lo)}–${money(hi)}` : money(lo);
  };

  return (
    <div style={{ marginTop: 48 }}>
      <div style={{ textAlign: 'center', marginBottom: 16 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: headingColor, margin: 0 }}>Beyond your plan</h2>
        <p style={{ fontSize: 14, color: mutedColor, marginTop: 8, maxWidth: 680, marginLeft: 'auto', marginRight: 'auto' }}>
          Every plan includes its allowances above. Use beyond them is metered at these rates.
        </p>
      </div>
      <div style={{ border: `1px solid ${BORDER}`, borderRadius: 16, background: CARD_BG, overflow: 'hidden' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr style={{ background: SURFACE_2 }}>
              {['Unit', 'Billed', 'Rate'].map((h, i) => (
                <th
                  key={h}
                  style={{
                    padding: '8px 20px',
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: 0.6,
                    textTransform: 'uppercase',
                    color: TEXT_MUTED,
                    textAlign: i === 2 ? 'right' : 'left',
                    borderBottom: `1px solid ${BORDER}`,
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((k, i) => {
              const copy = UNIT_COPY[k] as UnitCopy;
              const border = i === rows.length - 1 ? 'none' : `1px solid ${BORDER}`;
              return (
                <tr key={k}>
                  <td style={{ padding: '12px 20px', fontSize: 14, color: BLACK, fontWeight: 500, borderBottom: border }}>
                    {copy.label}
                  </td>
                  <td style={{ padding: '12px 20px', fontSize: 13.5, color: TEXT_MUTED, borderBottom: border }}>
                    {copy.per}
                  </td>
                  <td style={{ padding: '12px 20px', fontSize: 14, color: BLACK, fontWeight: 600, textAlign: 'right', whiteSpace: 'nowrap', borderBottom: border }}>
                    {rateLabel(k)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p style={{ fontSize: 12, color: mutedColor, textAlign: 'center', marginTop: 14 }}>
        Standard Brand overage is metered monthly at these rates. Enterprise allowances and rates are set per contract.
      </p>
    </div>
  );
}
